/**
 * Every parent-facing string for recurring events (YAR-443, Option B), in one
 * place for design / product review. There is no Figma for this yet.
 *
 * Weekday and month names are NOT here: they come from `Intl` so they follow
 * the locale (see `lib/recurrence.ts`).
 */
export const RECURRENCE_COPY = {
  // ── Review card ────────────────────────────────────────────────────
  /** Open-ended rule. `pattern` is e.g. "every Monday and Wednesday". */
  repeatsOpenEnded: (pattern: string) => `Repeats ${pattern}`,
  /**
   * Shown under the open-ended line. `date` is the first session, e.g.
   * "Oct 8" — short enough to stay on one or two lines on a phone card.
   */
  firstSessionOnlyHint: (date: string) =>
    `Without an end date, only ${date} is added.`,
  /** "through Dec 18" */
  through: (date: string) => `through ${date}`,
  sessions: (count: number, capped: boolean) =>
    capped
      ? `${count}+ sessions`
      : `${count} ${count === 1 ? 'session' : 'sessions'}`,
  /** Joins the bounded summary: "Every Tuesday · through Dec 18 · 12 sessions". */
  separator: ' · ',
  endDatePlaceholder: 'Add end date',
  clearEndDate: 'Remove end date',
  clearEndDateAria: 'Remove the end date and add only the first session',
  endDateBeforeStart: "The end date can't be before the first session.",
  endDateTooFar: 'The end date must be within a year of the first session.',
  saveError: "Couldn't save the end date. Please try again.",
  saving: 'Saving…',

  // ── Weekday pattern (interval-aware) ──────────────────────────────
  /** interval 1: "every Tuesday" */
  everyWeek: (days: string) => `every ${days}`,
  /** interval 2: "every other Tuesday" */
  everyOtherWeek: (days: string) => `every other ${days}`,
  /** interval n ≥ 3: "every 3 weeks on Tuesday" */
  everyNWeeks: (n: number, days: string) => `every ${n} weeks on ${days}`,

  // ── Schedule / Today cards and event detail ───────────────────────
  // A series with no end date reuses `repeatsOpenEnded` ("Repeats every
  // Tuesday"): only one occurrence exists, so the day is the useful part.
  repeatsWeekly: 'Repeats weekly',
  repeatsEveryOtherWeek: 'Repeats every other week',
  repeatsEveryNWeeks: (n: number) => `Repeats every ${n} weeks`,

  // ── Deleting an event of a series ─────────────────────────────────
  // One trash can: on a series event it asks what to delete, the way
  // Google / Apple calendars do.
  deleteSeriesTitle: 'Delete recurring event',
  deleteScopeThis: 'This event',
  deleteScopeFollowing: 'This and following events',
  deleteScopeAll: 'All events',
  /** Appended to a scope's label: "This and following events (4)". */
  deleteScopeCount: (label: string, count: number) => `${label} (${count})`,
  deleteSeriesConfirm: 'Delete',
  deleteSeriesCancel: 'Cancel',
  deleteSeriesCountError: "Couldn't check the series. Please try again.",
  /** Also shown when the delete removed nothing (e.g. RLS matched 0 rows). */
  deleteSeriesError: "Couldn't delete these events. Please try again.",
} as const;
